/**
 * Customer card — "Atsiskaitymai" (Settlements) tab (pr5-contract.md §1).
 *
 * Debt here is the RESULT of the documents the mirror holds (sales, sales
 * returns, purchases, purchase returns) — never copied from Finvalda.
 * Finvalda's client balance is imported only as a control figure: matching it
 * means nothing was missed; a mismatch is surfaced, not hidden or "fixed" by
 * substituting Finvalda's number.
 */
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Field, Label, Switch } from "@headlessui/react";
import clsx from "clsx";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
} from "lucide-react";
import Currency from "components/format/currency/currency";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
} from "components/modalx/modalx.slice";
import { fetchLedger } from "services/customer-ledger.service";
import {
  CustomerLedgerItem,
  CustomerLedgerResponse,
} from "types/CustomerLedger";
import { formatPrettyDate } from "utils/helperFunctions";
import { ledgerKindLabel } from "utils/ledgerKind";
import useIsPhone from "features/files/use-is-phone";
import ErpInvoicesSection from "./erp-invoices-section";
import PhoneRowCard from "./phone-row-card";
import SettlementPayments from "./settlement-payments";

interface Props {
  companyId?: string;
  customerId?: string;
}

type TFunction = (key: string, options?: any) => string;

const PAGE_SIZE = 25;

const KIND_FILTERS: Array<{ value: string; i18nKey: string }> = [
  { value: "", i18nKey: "ledger_kind_all" },
  { value: "sale", i18nKey: "ledger_kind_sale" },
  { value: "sale_return", i18nKey: "ledger_kind_sale_return" },
  { value: "purchase", i18nKey: "ledger_kind_purchase" },
  { value: "purchase_return", i18nKey: "ledger_kind_purchase_return" },
];

const cardCls =
  "rounded-xl border border-gray-200 bg-white p-4 shadow-sm flex flex-col gap-1";
const labelCls = "text-xs font-medium text-gray-500";
const valueCls = "text-lg font-semibold text-gray-900";
// The cards sit in the tab's column, about two thirds of the page, so they
// split into more columns later than the window width alone would suggest.
const summaryGridCls = "grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6";

export default function CustomerSettlements({ companyId, customerId }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Documents (what was invoiced) and payments (what moved, and what is still
  // an advance) are two halves of the same balance — one tab, two views,
  // rather than a second tab nobody would connect to the debt above.
  const [view, setView] = useState<"documents" | "payments">("documents");
  const [kind, setKind] = useState<string>("");
  const [openOnly, setOpenOnly] = useState(false);
  const [page, setPage] = useState(1);

  const [data, setData] = useState<CustomerLedgerResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Guards against an in-flight request that is no longer the latest one
  // (rapid filter changes) from overwriting a newer response.
  const requestIdRef = useRef(0);

  const load = useCallback(
    (kindValue: string, openOnlyValue: boolean, pageValue: number) => {
      if (!companyId || !customerId) return;
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(false);
      fetchLedger(companyId, customerId, {
        kind: kindValue || undefined,
        openOnly: openOnlyValue,
        page: pageValue,
        pageSize: PAGE_SIZE,
        // Always requested (pr7-contract.md §Frontend/1: "keep it simple" —
        // the page size stays at 25, so this is one extra join, not a
        // per-row fetch) so the lines are already in hand when a row expands.
        withLines: true,
      })
        .then((response) => {
          if (requestId !== requestIdRef.current) return;
          setData(response);
          setLoading(false);
        })
        .catch(() => {
          if (requestId !== requestIdRef.current) return;
          setError(true);
          setLoading(false);
        });
    },
    [companyId, customerId]
  );

  useEffect(() => {
    load(kind, openOnly, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, customerId, kind, openOnly, page]);

  const handleKindChange = (value: string) => {
    setKind(value);
    setPage(1);
  };

  const handleOpenOnlyChange = (value: boolean) => {
    setOpenOnly(value);
    setPage(1);
  };

  const retry = () => load(kind, openOnly, page);

  const openErpInvoice = (invoiceId: string) => {
    dispatch(
      setModalxOptions({
        title: t("view_invoice", { defaultValue: "View invoice" }),
      })
    );
    dispatch(setModalxComponent("InvoiceForm"));
    dispatch(setModalxComponentProps({ customerId, invoiceId, onDone: retry }));
  };

  if (loading && !data) {
    return <SettlementsSkeleton />;
  }

  if (error && !data) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {t("settlements_load_failed", { defaultValue: "Could not load settlements." })}
        <button
          type="button"
          className="ml-3 rounded-md border border-red-300 bg-white px-3 py-1 text-red-700 hover:bg-red-100"
          onClick={retry}
        >
          {t("retry", { defaultValue: "Retry" })}
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { summary } = data;

  const viewTabs: Array<{ value: "documents" | "payments"; i18nKey: string; defaultValue: string }> = [
    { value: "documents", i18nKey: "settlements_view_documents", defaultValue: "Documents" },
    { value: "payments", i18nKey: "settlements_view_payments", defaultValue: "Payments" },
  ];

  return (
    <div className="flex flex-col gap-5" data-testid="customer-settlements">
      <SummaryCards summary={summary} t={t} />

      <div className="flex gap-1.5" role="tablist" aria-label={t("settlements_view", { defaultValue: "View" })}>
        {viewTabs.map((tab) => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={view === tab.value}
            className={clsx(
              "rounded-lg px-3 py-1.5 text-sm font-medium transition-colors max-md:min-h-11",
              view === tab.value
                ? "bg-gray-900 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            )}
            onClick={() => setView(tab.value)}
          >
            {t(tab.i18nKey, { defaultValue: tab.defaultValue })}
          </button>
        ))}
      </div>

      {view === "payments" ? (
        <SettlementPayments companyId={companyId} customerId={customerId} />
      ) : summary.isMirrorBased === false ? (
        <>
          <ErpInvoicesSection invoices={data.erpInvoices} onOpen={openErpInvoice} />
          <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-500">
            {t("settlements_not_synced_yet", {
              defaultValue: "Accounting documents appear once the accounting mirror is synchronised.",
            })}
          </p>
        </>
      ) : (
        <>
          <ErpInvoicesSection invoices={data.erpInvoices} onOpen={openErpInvoice} />
          <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-1.5" role="group" aria-label={t("kind")}>
              {KIND_FILTERS.map((option) => (
                <button
                  key={option.value || "all"}
                  type="button"
                  className={clsx(
                    "rounded-full px-3 py-1 text-sm transition-colors max-md:py-3",
                    kind === option.value
                      ? "bg-gray-900 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                  onClick={() => handleKindChange(option.value)}
                >
                  {t(option.i18nKey)}
                </button>
              ))}
            </div>

            <Field className="flex items-center gap-2 max-md:min-h-11">
              <Switch
                checked={openOnly}
                onChange={handleOpenOnlyChange}
                className={clsx(
                  openOnly ? "bg-gray-900" : "bg-gray-200",
                  "relative inline-flex h-5 w-9 items-center rounded-full transition-colors max-md:h-6 max-md:w-11"
                )}
              >
                <span
                  className={clsx(
                    openOnly ? "translate-x-5 max-md:translate-x-6" : "translate-x-1",
                    "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform max-md:h-4 max-md:w-4"
                  )}
                />
              </Switch>
              <Label className="text-sm text-gray-700">{t("only_unpaid")}</Label>
            </Field>
          </div>

          <LedgerTable
            // Remounts on every filter/page change, which drops any expanded
            // rows along with the stale data instead of carrying them over.
            key={`${kind}-${openOnly}-${data.page}`}
            items={data.items}
            total={data.total}
            page={data.page}
            pageSize={data.pageSize}
            loading={loading}
            t={t}
            onPrev={() => setPage((current) => Math.max(1, current - 1))}
            onNext={() => setPage((current) => current + 1)}
          />
        </>
      )}
    </div>
  );
}

function SummaryCards({
  summary,
  t,
}: {
  summary: CustomerLedgerResponse["summary"];
  t: TFunction;
}) {
  return (
    <div className={summaryGridCls}>
      <div className={cardCls}>
        <span className={labelCls}>{t("receivable")}</span>
        <span className={valueCls}>
          <Currency value={summary.receivable} isSymbol isDecimal />
        </span>
      </div>
      <div className={cardCls}>
        <span className={labelCls}>{t("payable")}</span>
        <span className={valueCls}>
          <Currency value={summary.payable} isSymbol isDecimal />
        </span>
      </div>
      <div className={cardCls}>
        <span className={labelCls}>{t("debt")}</span>
        <span
          className={clsx(
            valueCls,
            typeof summary.debt === "number" && summary.debt < 0 && "text-red-600"
          )}
        >
          <Currency value={summary.debt} isSymbol isDecimal />
        </span>
        <AdvancesLine summary={summary} t={t} />
        <ReconciliationLine summary={summary} t={t} />
      </div>
      <div className={cardCls}>
        <span className={labelCls}>{t("overdue_debt")}</span>
        <span
          className={clsx(
            valueCls,
            typeof summary.overdueDebt === "number" &&
              summary.overdueDebt < 0 &&
              "text-red-600"
          )}
        >
          <Currency value={summary.overdueDebt} isSymbol isDecimal />
        </span>
        <span className="text-xs text-gray-500">
          {t("overdue_receivable_payable_line", {
            defaultValue: "receivable {{receivable}} / payable {{payable}}",
            receivable: formatMoney(summary.overdueReceivable),
            payable: formatMoney(summary.overduePayable),
          })}
        </span>
      </div>
      <div className={cardCls}>
        <span className={labelCls}>{t("sales_turnover_excl_vat")}</span>
        <span className={valueCls}>
          <Currency value={summary.salesTurnover} isSymbol isDecimal />
        </span>
      </div>
      <div className={cardCls}>
        <span className={labelCls}>{t("purchase_turnover_excl_vat")}</span>
        <span className={valueCls}>
          <Currency value={summary.purchaseTurnover} isSymbol isDecimal />
        </span>
      </div>
    </div>
  );
}

// The advance half of the debt, shown only when there is one: it is the part
// no document explains, so a manager comparing our number with accounting
// needs to see it spelled out rather than buried in `receivable`.
function AdvancesLine({
  summary,
  t,
}: {
  summary: CustomerLedgerResponse["summary"];
  t: TFunction;
}) {
  const receivable = summary.advanceReceivable || 0;
  const payable = summary.advancePayable || 0;
  if (receivable === 0 && payable === 0) {
    return null;
  }
  return (
    <span className="text-xs text-gray-500">
      {t("debt_advances_line", {
        defaultValue: "incl. advances: paid {{paid}} / received {{received}}",
        paid: formatMoney(receivable),
        received: formatMoney(payable),
      })}
    </span>
  );
}

function formatMoney(value: number | null): string {
  if (value === null || value === undefined) return "-";
  return value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function ReconciliationLine({
  summary,
  t,
}: {
  summary: CustomerLedgerResponse["summary"];
  t: TFunction;
}) {
  const balanceAtTitle = summary.finvaldaBalanceAt
    ? formatPrettyDate(summary.finvaldaBalanceAt, "lt-LT", true)
    : undefined;

  if (summary.isReconciled) {
    return (
      <span
        className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-green-700"
        title={balanceAtTitle}
      >
        <CheckCircle2 className="h-3.5 w-3.5" />
        {t("reconciled_with_finvalda")}
      </span>
    );
  }

  if (summary.balanceDifference != null && !summary.isReconciled) {
    return (
      <div className="mt-1 flex flex-col gap-0.5" title={balanceAtTitle}>
        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {t("not_reconciled_with_finvalda", {
            balance: formatMoney(summary.finvaldaBalance),
          })}
          {", "}
          {t("unexplained_amount").toLowerCase()}
          {": "}
          {formatMoney(summary.balanceDifference)}
        </span>
        {/* A live reconciliation run found documents explain Finvalda's
            balance for only ~75% of clients — the rest is almost always
            unallocated payments/advances/loans Finvalda's API does not
            expose, not a real gap in our documents. */}
        <span className="pl-5 text-xs text-gray-400">
          {t("finvalda_mismatch_hint")}
        </span>
      </div>
    );
  }

  return (
    <span
      className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-gray-400"
      title={balanceAtTitle}
    >
      <HelpCircle className="h-3.5 w-3.5" />
      {t("finvalda_balance_unknown")}
    </span>
  );
}

function LedgerTable({
  items,
  total,
  page,
  pageSize,
  loading,
  t,
  onPrev,
  onNext,
}: {
  items: CustomerLedgerItem[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  t: TFunction;
  onPrev: () => void;
  onNext: () => void;
}) {
  const isPhone = useIsPhone();
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const columnCount = 10; // expand chevron + the 9 data columns below

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div
      className={clsx(
        "overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm",
        loading && "opacity-60"
      )}
    >
      {isPhone ? (
        <LedgerCards items={items} expandedIds={expandedIds} onToggle={toggleExpanded} t={t} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500">
                <th className="w-8 px-2 py-2" aria-hidden="true" />
                <th className="px-3 py-2">{t("ledger_col_date", { defaultValue: "Date" })}</th>
                <th className="px-3 py-2">{t("ledger_col_kind", { defaultValue: "Type" })}</th>
                <th className="px-3 py-2">{t("ledger_col_journal", { defaultValue: "Journal" })}</th>
                <th className="px-3 py-2">{t("ledger_col_series_number", { defaultValue: "Series / No." })}</th>
                <th className="px-3 py-2">{t("ledger_col_due_date", { defaultValue: "Due" })}</th>
                <th className="px-3 py-2 text-right">{t("ledger_col_amount_doc", { defaultValue: "Amount" })}</th>
                <th className="px-3 py-2 text-right">{t("ledger_col_amount_eur", { defaultValue: "Amount EUR" })}</th>
                <th className="px-3 py-2 text-right">{t("ledger_col_paid", { defaultValue: "Paid" })}</th>
                <th className="px-3 py-2 text-right">{t("ledger_col_debt", { defaultValue: "Debt" })}</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="px-3 py-8 text-center text-gray-400">
                    {t("no_data", { defaultValue: "No data" })}
                  </td>
                </tr>
              ) : (
                items.map((item) => {
                  const isExpanded = expandedIds.has(item.id);
                  return (
                    <Fragment key={item.id}>
                      <tr className="border-b border-gray-100 last:border-b-0">
                        <td className="px-2 py-2">
                          <button
                            type="button"
                            aria-label={isExpanded ? t("collapse") : t("expand")}
                            aria-expanded={isExpanded}
                            className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-gray-100"
                            onClick={() => toggleExpanded(item.id)}
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </button>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2">{formatPrettyDate(item.opDate)}</td>
                        <td className="whitespace-nowrap px-3 py-2">{ledgerKindLabel(t, item.kind)}</td>
                        <td className="whitespace-nowrap px-3 py-2">
                          <span>{item.journal || "-"}</span>
                          {item.isExcluded && (
                            <span className="ml-1.5 inline-flex items-center rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-500">
                              {t("excluded_from_turnover")}
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2">
                          {[item.series, item.number].filter(Boolean).join(" / ") || "-"}
                        </td>
                        <td
                          className={clsx(
                            "whitespace-nowrap px-3 py-2",
                            item.isOverdue && "text-red-600"
                          )}
                        >
                          {item.dueDate ? formatPrettyDate(item.dueDate) : "-"}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right">
                          {formatMoney(item.amountDoc)} {item.currency}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right">
                          <Currency value={item.grossEur} isSymbol isDecimal />
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right">
                          <Currency value={item.paidEur} isSymbol isDecimal />
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right">
                          <Currency value={item.debtEur} isSymbol isDecimal />
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="border-b border-gray-100 last:border-b-0 bg-gray-50">
                          <td colSpan={columnCount} className="px-3 py-3">
                            <DocumentLines item={item} t={t} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between border-t border-gray-100 px-3 py-2 text-sm text-gray-500">
        <span>
          {total === 0
            ? t("no_data", { defaultValue: "No data" })
            : t("pagination_range", {
                defaultValue: "{{start}}–{{end}} of {{total}}",
                start,
                end,
                total,
              })}
        </span>
        <div className="flex gap-1">
          <button
            type="button"
            aria-label={t("desk_page_prev", { defaultValue: "Previous" })}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 disabled:opacity-40 max-md:h-11 max-md:w-11"
            disabled={page <= 1}
            onClick={onPrev}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label={t("desk_page_next", { defaultValue: "Next" })}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 disabled:opacity-40 max-md:h-11 max-md:w-11"
            disabled={end >= total}
            onClick={onNext}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// The phone's version of LedgerTable's rows (see PhoneRowCard).
function LedgerCards({
  items,
  expandedIds,
  onToggle,
  t,
}: {
  items: CustomerLedgerItem[];
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
  t: TFunction;
}) {
  if (items.length === 0) {
    return (
      <p className="px-3 py-8 text-center text-sm text-gray-400">
        {t("no_data", { defaultValue: "No data" })}
      </p>
    );
  }

  return (
    <div>
      {items.map((item) => (
        <PhoneRowCard
          key={item.id}
          title={`${formatPrettyDate(item.opDate)} · ${ledgerKindLabel(t, item.kind)}`}
          headline={
            <>
              {t("ledger_col_debt", { defaultValue: "Debt" })}:{" "}
              <Currency value={item.debtEur} isSymbol isDecimal />
            </>
          }
          isExpanded={expandedIds.has(item.id)}
          onToggle={() => onToggle(item.id)}
          details={<DocumentLines item={item} t={t} />}
        >
          <div>{[item.series, item.number].filter(Boolean).join(" / ") || "-"}</div>
          <div>
            {item.journal || "-"}
            {item.isExcluded && (
              <span className="ml-1.5 inline-flex items-center rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-500">
                {t("excluded_from_turnover")}
              </span>
            )}
          </div>
          <div className={clsx(item.isOverdue && "text-red-600")}>
            {t("ledger_col_due_date", { defaultValue: "Due" })}:{" "}
            {item.dueDate ? formatPrettyDate(item.dueDate) : "-"}
          </div>
          <div>
            {t("ledger_col_amount_eur", { defaultValue: "Amount EUR" })}:{" "}
            <Currency value={item.grossEur} isSymbol isDecimal />
            {item.currency && item.currency !== "EUR" && (
              <span className="text-xs text-gray-400">
                {" "}
                ({formatMoney(item.amountDoc)} {item.currency})
              </span>
            )}
          </div>
          <div>
            {t("ledger_col_paid", { defaultValue: "Paid" })}:{" "}
            <Currency value={item.paidEur} isSymbol isDecimal />
          </div>
        </PhoneRowCard>
      ))}
    </div>
  );
}

/**
 * A document's `document_lines`, shown when its row is expanded (fetched
 * eagerly with `?withLines=1` — pr7-contract.md §Frontend/1 — so this never
 * triggers a request of its own).
 */
function DocumentLines({ item, t }: { item: CustomerLedgerItem; t: TFunction }) {
  const lines = item.lines || [];

  if (lines.length === 0) {
    return (
      <span className="text-sm text-gray-400">
        {t("ledger_no_lines", { defaultValue: "no lines" })}
      </span>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="text-left text-gray-500">
            <th className="px-2 py-1">{t("ledger_line_col_no", { defaultValue: "No." })}</th>
            <th className="px-2 py-1">{t("ledger_line_col_item", { defaultValue: "Item" })}</th>
            <th className="px-2 py-1 text-right">{t("ledger_line_col_qty", { defaultValue: "Qty" })}</th>
            <th className="px-2 py-1 text-right">
              {t("ledger_line_col_unit_price", { defaultValue: "Unit price" })}
            </th>
            <th className="px-2 py-1 text-right">{t("ledger_line_col_net", { defaultValue: "Net" })}</th>
            <th className="px-2 py-1 text-right">{t("ledger_line_col_vat", { defaultValue: "VAT" })}</th>
            <th className="px-2 py-1 text-right">{t("ledger_line_col_gross", { defaultValue: "Gross" })}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.lineNo} className="border-t border-gray-200">
              <td className="px-2 py-1">{line.lineNo}</td>
              <td className="px-2 py-1">
                {[line.sku, line.title].filter(Boolean).join(" — ") || "-"}
              </td>
              <td className="px-2 py-1 text-right">{line.quantity}</td>
              <td className="px-2 py-1 text-right">
                <Currency value={line.unitPriceEur} isSymbol isDecimal />
              </td>
              <td className="px-2 py-1 text-right">
                <Currency value={line.netEur} isSymbol isDecimal />
              </td>
              <td className="px-2 py-1 text-right">
                <Currency value={line.vatEur} isSymbol isDecimal />
              </td>
              <td className="px-2 py-1 text-right">
                <Currency value={line.grossEur} isSymbol isDecimal />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {item.linesTruncated && (
        <span className="text-xs text-gray-400">
          {t("ledger_lines_truncated_note", { defaultValue: "…more lines not shown" })}
        </span>
      )}
    </div>
  );
}

function SettlementsSkeleton() {
  return (
    <div className="flex flex-col gap-5" data-testid="customer-settlements-skeleton">
      <div className={summaryGridCls}>
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className={cardCls}>
            <div className="h-3 w-20 animate-pulse rounded bg-gray-200" />
            <div className="mt-2 h-5 w-24 animate-pulse rounded bg-gray-200" />
          </div>
        ))}
      </div>
      <div className="h-64 animate-pulse rounded-xl border border-gray-200 bg-gray-50" />
    </div>
  );
}
